/**
 * SaveTemplateModal — Persist a TaskFormState snapshot as a reusable template.
 *
 * Saves to backend via useTaskTemplates hook (DynamoDB + Valkey).
 * New templates are created as Draft — require approval to publish.
 */

import {
  Button,
  Group,
  Modal,
  Select,
  Stack,
  Text,
  TextInput,
  Textarea,
  TagsInput,
} from '@mantine/core';
import { IconCheck, IconDeviceFloppy, IconLoader } from '@tabler/icons-react';
import React, { useState } from 'react';
import type { TaskFormState } from './taskFormState';
import { useTaskTemplates } from '../hooks/useTaskTemplates';

export interface SaveTemplateModalProps {
  opened: boolean;
  onClose: () => void;
  /** Current form state to save */
  formState: TaskFormState;
  /** Called after successful save */
  onSaved?: () => void;
}

const CATEGORIES = [
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'finance', label: 'Finance' },
  { value: 'legal', label: 'Legal' },
  { value: 'customer-service', label: 'Customer Service' },
  { value: 'compliance', label: 'Compliance' },
  { value: 'integration', label: 'Integration' },
  { value: 'general', label: 'General' },
];

export const SaveTemplateModal: React.FC<SaveTemplateModalProps> = ({
  opened,
  onClose,
  formState,
  onSaved,
}) => {
  const [templateName, setTemplateName] = useState(formState.name || '');
  const [templateDesc, setTemplateDesc] = useState('');
  const [category, setCategory] = useState<string>('general');
  const [templateTags, setTemplateTags] = useState<string[]>(formState.tags || []);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const { saveTemplate } = useTaskTemplates();

  const handleSave = async () => {
    if (!templateName.trim() || saving) return;
    setSaving(true);

    const id = await saveTemplate(formState, {
      name: templateName.trim(),
      description: templateDesc.trim(),
      category,
      tags: templateTags,
    });

    setSaving(false);

    if (id) {
      setSaved(true);
      setTimeout(() => {
        setSaved(false);
        onSaved?.();
        onClose();
      }, 800);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="xs">
          <IconDeviceFloppy size={20} />
          <Text fw={600}>Save as Template</Text>
        </Group>
      }
      size="md"
    >
      <Stack gap="md">
        <TextInput
          label="Template Name"
          required
          placeholder="e.g. HIPAA Medication Review"
          value={templateName}
          onChange={(e) => setTemplateName(e.currentTarget.value)}
        />

        <Textarea
          label="Description"
          placeholder="What is this template for?"
          rows={2}
          value={templateDesc}
          onChange={(e) => setTemplateDesc(e.currentTarget.value)}
        />

        <Select
          label="Category"
          data={CATEGORIES}
          value={category}
          onChange={(v) => setCategory(v || 'general')}
        />

        <TagsInput
          label="Tags"
          placeholder="Add tags..."
          value={templateTags}
          onChange={setTemplateTags}
        />

        <Text size="xs" c="dimmed">
          Templates are saved as drafts. Submit for approval to make them available to others.
        </Text>

        <Group justify="flex-end" mt="sm">
          <Button variant="subtle" onClick={onClose}>
            Cancel
          </Button>
          <Button
            leftSection={
              saving ? <IconLoader size={16} /> : saved ? <IconCheck size={16} /> : <IconDeviceFloppy size={16} />
            }
            color={saved ? 'green' : 'teal'}
            onClick={handleSave}
            disabled={!templateName.trim() || saving || saved}
            loading={saving}
          >
            {saved ? 'Saved!' : 'Save Template'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default SaveTemplateModal;
